import { ElectronAPI } from '#~/electron/index.js';
import { isElectronApp } from '#~/electron/utils.js';
import { post } from '#~/notifications/utils.js';
import { useLastRoomIdPref, useLinkedRoom } from '#~/room/hook_last_id.js';
import type { DeviceEgressSelectorType } from '#~/statequery/selectors/device-egress.js';
import { useEgressErrorStore } from '#~/store/index.js';
import { StatusEgressState, StatusEgressReason } from '@tryiris-ai/common.protojs/agent/v1/agent';
import { codecBlockedNoticeId, getCodecWarning } from '../components/codec-warning.js';
import { SourceCard } from '../components/source-card.js';
import { Sel, STagNew } from '#~/stag/index.js';
import fImg from './folder.png';
import { useRecording, useRecordingPath } from './use-recording.js';
import { getErrorMessage, getRecordingConfig } from './utils.js';
import {
  useAnalytics,
  EVENT_CATEGORIES,
  EVENT_NAMES,
  RECORDING_PROPERTIES
} from '#~/analytics/index.js';
import { Arrow, Tooltip } from '@studio/design-system/tooltip';
import { DialogClose } from '@radix-ui/react-dialog';
import { Button } from '@studio/design-system/button';
import { motion } from '@studio/design-system/motion';
import { type ReactElement, useMemo, useCallback, useState } from 'react';
import { useEffectOnce } from '@studio/react-utils/hooks';
import type { Device } from '@studio/models/device';

/** `<Output>` component. */
export const Recording = (): ReactElement => {
  const { roomId: linkedRoomId } = useLinkedRoom();
  const { track } = useAnalytics();

  const [roomId] = useLastRoomIdPref();
  const recording = useRecording(roomId);
  const {
    initialDeviceSelectionMap,
    deviceSelectionMap,
    setDeviceSelectionMap,
    recordingDevices,
    stopRecording: unsafeStopRecording,
    saveChanges: unsafeSaveChanges,
    sources,
    blockedReasonByDevice
  } = recording;

  const isRecording = recordingDevices.length > 0;

  const [unsafeIsRecordingStarting, setUnsafeIsRecordingStarting] = useState(false);
  const [unsafeIsRecordingStopping, setUnsafeIsRecordingStopping] = useState(false);

  const isRecordingStarting =
    !isRecording &&
    (sources.some(
      (device) => device.egress?.recordingStatus?.state === StatusEgressState.EGRESS_STATE_STARTING
    ) ||
      unsafeIsRecordingStarting);
  const isRecordingStopping = isRecording && unsafeIsRecordingStopping;

  const stopRecording = useCallback(() => {
    unsafeStopRecording();
    setUnsafeIsRecordingStopping(true);

    setTimeout(() => {
      setUnsafeIsRecordingStopping(false);
    }, 10_000);
  }, [setUnsafeIsRecordingStopping, unsafeStopRecording]);

  const saveChanges = useCallback(() => {
    const { clear } = useEgressErrorStore.getState();
    // Denied codecs are excluded from clear(), the starting spinner, and analytics.
    const startable = Object.entries(deviceSelectionMap).filter(([deviceId, isSelected]) => {
      if (!isSelected || blockedReasonByDevice[deviceId]) return false;
      const source = sources.find((s) => s.id === deviceId);
      if (source?.type === 'video' && getCodecWarning('recording', source.videoCodec)) {
        return false;
      }
      return true;
    });
    for (const [deviceId] of startable) {
      clear(deviceId, 'recording');
    }
    unsafeSaveChanges();
    const isStarting = startable.length > 0;

    if (isStarting) {
      setUnsafeIsRecordingStarting(true);

      // Track the start recording event
      const selectedDeviceIds = startable.map(([deviceId]) => deviceId);

      track(EVENT_NAMES.START_RECORDING, EVENT_CATEGORIES.RECORDING, {
        [RECORDING_PROPERTIES.DEVICE_IDS]: selectedDeviceIds
      });

      setTimeout(() => {
        setUnsafeIsRecordingStarting(false);
      }, 10_000);
    } else {
      setUnsafeIsRecordingStopping(true);

      setTimeout(() => {
        setUnsafeIsRecordingStopping(false);
      }, 10_000);
    }
  }, [
    setUnsafeIsRecordingStarting,
    unsafeSaveChanges,
    deviceSelectionMap,
    sources,
    blockedReasonByDevice
  ]);

  const hasChanges = useMemo(() => {
    return Object.keys(deviceSelectionMap).some(
      (key) =>
        deviceSelectionMap[key] !== initialDeviceSelectionMap[key] &&
        (!deviceSelectionMap[key] || !blockedReasonByDevice[key])
    );
  }, [initialDeviceSelectionMap, deviceSelectionMap, blockedReasonByDevice]);

  const onSelection = useCallback((deviceId: string) => {
    setDeviceSelectionMap((prev) => ({ ...prev, [deviceId]: !prev[deviceId] }));
  }, []);

  const {
    savePath: saveRecordingPath,
    getDirFromState,
    saveToPersistentStorage,
    getDefaultRecordingPath
  } = useRecordingPath();

  const videoSources = sources.filter((v) => v.type === 'video' && !v.id.startsWith('tmp'));
  const audioSources = sources.filter((v) => v.type === 'audio' && !v.id.startsWith('tmp'));

  const [newPath, setNewPath] = useState('');
  const [isSelecting, setIsSelecting] = useState(false);
  useEffectOnce(() => {
    const fetchPath = async () => {
      try {
        let candidatePath = await getDirFromState();
        let path = '';
        if (!candidatePath) {
          // Path is not saved in the state (probably first time using app),
          // fetch it from the electron app a default one.
          path = await getDefaultRecordingPath();
        } else {
          path = candidatePath;
        }
        // const path = await ElectronAPI.getRecordingPath();
        setNewPath(path);
      } catch (error) {
        console.error('Error fetching path', error);
      }
    };
    fetchPath();
  }); // We need to run it only once when it mounts to fill the path.

  const handleOpenRecordingPath = () => {
    try {
      ElectronAPI.openRecordingPath();
    } catch (error) {
      console.error('Error opening recording path', error);
    }
  };

  const handleSelectDirectory = async () => {
    try {
      setIsSelecting(true);
      const path = await ElectronAPI.selectRecordingDirectory();
      if (!path) {
        // The user pressed cancel
        return;
      }
      setNewPath(path);
      // Save in state sync
      await saveRecordingPath(path);
      // Save in persistent storage
      await saveToPersistentStorage();
    } finally {
      setIsSelecting(false);
    }
  };

  return (
    <>
      <div className='overflow-auto px-40 pb-32'>
        <div>
          <h2>Recording</h2>
          <div className='text-sm text-muted'>Select the sources you would like to record</div>
        </div>
        <div className='my-40 border-t-1 border-base-50' />

        {roomId && isElectronApp() && roomId === linkedRoomId && (
          <div className='mb-32'>
            <div className='text-sm'>Save my files to</div>
            <div className='mt-6 flex items-center gap-8'>
              <div className='flex items-center gap-4 rounded-4 bg-base-100 px-12 py-10 text-muted'>
                <img src={fImg} />
                {newPath}
              </div>
              <div className='flex gap-8'>
                <Button
                  variant='secondary'
                  size='lg'
                  disabled={!isElectronApp() || !newPath}
                  onClick={handleOpenRecordingPath}
                >
                  Open
                </Button>
                <Button
                  variant='secondary'
                  size='lg'
                  disabled={!isElectronApp() || isSelecting}
                  onClick={handleSelectDirectory}
                >
                  Change
                </Button>
              </div>
            </div>
          </div>
        )}

        <div>
          <h2>Select sources</h2>
          <div className='text-sm text-muted'>
            Select the sources you would like to include in recording
          </div>
        </div>
        <div className='mt-32 flex gap-x-40'>
          <div className='flex flex-1 flex-col gap-12'>
            <h3>Video sources ({videoSources.length})</h3>
            {videoSources.length === 0 ? (
              <p className='text-muted'>No video sources are connected.</p>
            ) : (
              <SourcesList
                type='video'
                sources={videoSources}
                selected={deviceSelectionMap}
                blockedReasonByDevice={blockedReasonByDevice}
                onSelection={onSelection}
              />
            )}
          </div>
          <div className='flex flex-1 flex-col gap-12'>
            <h3>Audio sources ({audioSources.length})</h3>
            {audioSources.length === 0 ? (
              <p className='text-muted'>No audio sources are connected.</p>
            ) : (
              <SourcesList
                type='audio'
                sources={audioSources}
                selected={deviceSelectionMap}
                blockedReasonByDevice={blockedReasonByDevice}
                onSelection={(deviceId) => {
                  setDeviceSelectionMap((prev) => ({ ...prev, [deviceId]: !prev[deviceId] }));
                }}
              />
            )}
          </div>
        </div>
      </div>
      <div className='grow'></div>
      {/* Footer controls carry STags so HIL specs never reach them by accessible
          name — the labels here are state-dependent (Start Recording / Save
          Changes / Starting...). Per rule: playwright-stag-selectors-only
          (ai.knowledge/platform). The Cancel tag is shared with the Output
          footer: only the mounted tab's copy is visible. */}
      <div className='flex justify-between bg-base-100 px-40 py-24'>
        <DialogClose>
          <Button size='lg' variant='secondary' {...STagNew(Sel.Production, 'cancel')}>
            Cancel
          </Button>
        </DialogClose>
        <div className='flex gap-12'>
          <Button
            size='lg'
            variant={isRecording ? 'primary' : hasChanges ? 'danger' : 'secondary'}
            disabled={!hasChanges || isRecordingStarting || isRecordingStopping}
            onClick={saveChanges}
            {...STagNew(Sel.ProductionRecording, 'save')}
          >
            {isRecordingStopping
              ? 'Stopping...'
              : isRecordingStarting
                ? isRecording
                  ? 'Saving...'
                  : 'Starting...'
                : isRecording
                  ? 'Save Changes'
                  : 'Start Recording'}
          </Button>
          {isRecording && (
            <Button
              size='lg'
              variant={'dangerSecondary'}
              onClick={stopRecording}
              {...STagNew(Sel.ProductionRecording, 'stop_all')}
            >
              Stop All Recordings
            </Button>
          )}
        </div>
      </div>
    </>
  );
};
export const Source = ({
  source,
  selected,
  onSelection,
  type,
  diskBlockedReason
}: {
  source: DeviceEgressSelectorType;
  diskBlockedReason?: string | undefined;
  selected: Record<string, boolean>;
  onSelection: (deviceId: Device['id']) => void;
  type: 'audio' | 'video';
}) => {
  const deviceSelected = selected[source.id] ?? false;

  const selectionCallback = useCallback(() => {
    onSelection(source.id);
  }, [source.id]);

  const codecWarning =
    type === 'video' ? getCodecWarning('recording', source.videoCodec) : undefined;
  const reactiveError = getErrorMessage(
    source.egress?.recordingStatus?.state,
    source.egress?.recordingStatus?.error
  );
  const { dismiss, isDismissed } = useEgressErrorStore();
  const visibleError =
    (diskBlockedReason &&
      source.egress?.recordingStatus?.reason === StatusEgressReason.EGRESS_REASON_DISK_FULL) ||
    isDismissed(source.id, 'recording', reactiveError)
      ? undefined
      : reactiveError;

  const card = (
    <SourceCard
      isSelectable={!diskBlockedReason}
      stagId={source.id}
      name={source.name}
      isActive={source.egress?.recordingStatus?.state === StatusEgressState.EGRESS_STATE_ACTIVE}
      type={type}
      unselectableReason={codecWarning}
      onBlockedSelection={() => {
        post({
          // Pinned id: the notice renders it as its STag uniqueID, so a HIL
          // spec can address THIS toast rather than search the stack by copy.
          id: codecBlockedNoticeId('recording', source.id),
          title: 'Recording unavailable for this source',
          message: `${source.name}: ${codecWarning}`,
          severity: 'warning'
        });
      }}
      error={visibleError}
      onDismissError={
        visibleError ? () => dismiss(source.id, 'recording', visibleError) : undefined
      }
      config={getRecordingConfig(source)}
      selected={deviceSelected}
      onSelection={selectionCallback}
    />
  );
  return diskBlockedReason ? (
    <Tooltip
      zIndex={100}
      trigger={
        <div
          tabIndex={0}
          aria-label={diskBlockedReason}
          className='focus-visible:ring-primary/50 cursor-not-allowed opacity-50 focus-visible:ring-2 [&_button:disabled]:cursor-not-allowed'
          {...STagNew(Sel.ProductionRecording, 'disk-blocked', source.id)}
        >
          {card}
        </div>
      }
    >
      <div className='rounded-4 bg-base-100 px-12 py-8 text-sm text-foreground'>
        {diskBlockedReason}
        <Arrow className='fill-base-100' />
      </div>
    </Tooltip>
  ) : (
    card
  );
};

const SourcesList = ({
  sources,
  selected,
  onSelection,
  type,
  blockedReasonByDevice
}: {
  sources: DeviceEgressSelectorType[];
  blockedReasonByDevice: Record<string, string>;
  selected: Record<string, boolean>;
  onSelection: (deviceId: string) => void;
  type: 'audio' | 'video';
}) => {
  const recordingSources = sources.filter(
    (source) => source.egress?.recordingStatus?.state === StatusEgressState.EGRESS_STATE_ACTIVE
  );
  const nonRecordingSources = sources.filter(
    (source) => source.egress?.recordingStatus?.state !== StatusEgressState.EGRESS_STATE_ACTIVE
  );

  return (
    <>
      {[
        ...recordingSources.map((source) => {
          return (
            <motion.div
              layout='position'
              key={source.id}
              layoutId={`device-recording-${source.id}`}
            >
              <Source
                type={type}
                source={source}
                key={source.id}
                selected={selected}
                diskBlockedReason={blockedReasonByDevice[source.id]}
                onSelection={onSelection}
              />
            </motion.div>
          );
        }),
        recordingSources.length > 0 && (
          <motion.div
            layout='position'
            layoutId='device-recording-divider'
            key='divider'
            className='h-20'
          />
        ),
        ...nonRecordingSources.map((source) => {
          return (
            <motion.div
              layout='position'
              key={source.id}
              layoutId={`device-recording-${source.id}`}
            >
              <Source
                source={source}
                type={type}
                key={source.id}
                selected={selected}
                diskBlockedReason={blockedReasonByDevice[source.id]}
                onSelection={onSelection}
              />
            </motion.div>
          );
        })
      ]}
    </>
  );
};
